import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Mail, MapPin, Phone } from 'lucide-react'
import { useState } from 'react'

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  })

  const handleInputChange = (e) => {
    const { id, value } = e.target
    setFormData(prev => ({
      ...prev,
      [id]: value
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const subject = `Quote Request: ${formData.service || 'General Inquiry'}`
    const body = `Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Service Required: ${formData.service}

Message:
${formData.message}`
    
    const mailtoLink = `mailto:info@hill-bros.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    window.location.href = mailtoLink
  }
  return (
    <div className="py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-center mb-8">Contact Us</h1>
        
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Get in Touch</CardTitle>
              <CardDescription>
                We're here to help with all your roofing and building needs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-semibold">Contact Address</p>
                  <p className="text-muted-foreground">
                    31 Connaught Road<br />Seaford<br />BN25 2PU
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-semibold">Yard Address</p>
                  <p className="text-muted-foreground">
                    Maritime House<br />Basin Road North<br />Brighton and Hove<br />Brighton<br />BN41 1WR
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-semibold">Email</p>
                  <a 
                    href="mailto:info@hill-bros.co.uk"
                    className="text-primary hover:underline"
                  >
                    info@hill-bros.co.uk
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-semibold">Phone</p>
                  <a 
                    href="tel:01273326200"
                    className="text-primary hover:underline"
                  >
                    01273 326200
                  </a>
                </div>
              </div>

              <div className="pt-4">
                <Button asChild size="lg" className="w-full">
                  <a href="mailto:info@hill-bros.co.uk">
                    <Mail className="mr-2 h-4 w-4" />
                    Email Us
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Request a Quote</CardTitle>
              <CardDescription>
                Contact us for a free, no-obligation quote
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium mb-1">
                    Service Required
                  </label>
                  <select
                    id="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                    required
                  >
                    <option value="">Select a service...</option>
                    <option value="roofing">Roofing</option>
                    <option value="flat-roofing">Flat Roofing</option>
                    <option value="general-building">General Building</option>
                    <option value="guttering-fascias">Guttering & Fascias</option>
                    <option value="leadwork">Leadwork</option>
                    <option value="repointing">Re-pointing</option>
                    <option value="lime-pointing">Lime Pointing</option>
                    <option value="render-repairs">Render Repairs</option>
                    <option value="damp-proofing">Damp Proofing</option>
                    <option value="loft-insulation">Loft Insulation</option>
                    <option value="plastering">Plastering</option>
                    <option value="decorating">Decorating</option>
                    <option value="sash-window-repairs">Sash Window Repairs</option>
                    <option value="drone-surveys">Drone Surveys</option>
                    <option value="chimney-demolition">Chimney Demolition</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-1">
                    How can we help?
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Please provide more details about your project..."
                    required
                  />
                </div>

                <Button type="submit" size="lg" className="w-full">
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        <Card className="max-w-4xl mx-auto mt-8">
          <CardHeader>
            <CardTitle>Service Areas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg mb-4">
              We proudly serve Brighton, Hove, and surrounding areas throughout East and West Sussex.
            </p>
            <p className="text-muted-foreground">
              Our service area includes: Brighton, Hove, Portslade, Southwick, Shoreham-by-Sea, 
              Lancing, Worthing, Littlehampton, Peacehaven, Newhaven, Seaford, Eastbourne, 
              Lewes, Burgess Hill, Haywards Heath, and all surrounding areas.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default Contact